import { ReactNode, useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import {
  Button,
  CalculateIcon,
  FileIcon,
  InputNumber,
  InputNumberVariantEnum,
  QuestionFillIcon,
  showToast,
  SparksIcon,
  Text,
  Tooltip,
} from '@mercai/clever'

import { getDateWithWeekdayOffset } from '../../helpers'
import { validateFields } from './logistic-data.helper'

import {
  QuotationAIComplementDataReasonFieldsProps,
  QuotationProps,
  TransportCompanyProps,
} from '@/types'

import { LogisticDataProps } from './logistic-data.type'

import { useCalculateLogisticCustomsCosts } from '@/services'
import { SectionHeader } from '../section-header'
import styles from './logistic-data.module.css'

export const LogisticData = ({
  position,
  isComplete,
  quotationValues,
  setQuotationValues,
  errors,
  getMetrics,
}: LogisticDataProps) => {
  const { t } = useTranslation('screenBusinessV2')

  const { mutateAsync, isPending } = useCalculateLogisticCustomsCosts()

  const getCustomsCosts = async () => {
    if (isPending) return

    const response = await mutateAsync({
      items: quotationValues?.products?.map((product) => ({
        cost: Number(product?.cost),
        quantity: product?.quantity,
      })),
      includeIva: quotationValues?.includeIva || false,
    })

    if (!response?.success) {
      return showToast({
        variant: 'error',
        title: response?.message || t('logisticDataCalculateCostsErrorTitle'),
      })
    }

    setQuotationValues((prev) => ({
      ...prev,
      customsFreightCost:
        response?.data?.customsCost || prev?.customsFreightCost,
    }))
  }

  const removeAiComplementFieldReason = (
    key: string,
    quotationValues?: Partial<QuotationProps>,
  ): Partial<QuotationProps> => {
    if (key === 'firstMileFreightCost') {
      delete quotationValues?.aiComplementData?.reasonFields?.firstMileCost
    }
    if (key === 'lastMileFreightCost') {
      delete quotationValues?.aiComplementData?.reasonFields?.lastMileCost
    }
    if (key === 'customsFreightCost') {
      delete quotationValues?.aiComplementData?.reasonFields?.customsCost
    }

    delete quotationValues?.aiComplementData?.reasonFields?.[
      key as keyof QuotationAIComplementDataReasonFieldsProps
    ]

    return {
      ...quotationValues,
      aiComplementData: {
        ...quotationValues?.aiComplementData,
        reasonFields: {
          ...quotationValues?.aiComplementData?.reasonFields,
        },
      },
    }
  }

  const onHandleValues = (
    key: string,
    value: number | string | boolean | undefined | TransportCompanyProps,
  ) => {
    const data = {
      ...quotationValues,
      ...removeAiComplementFieldReason(key, quotationValues),
      [key]: value,
    }

    setQuotationValues(data)
  }

  const getAIProps = (
    key: string,
  ): {
    variant?: keyof typeof InputNumberVariantEnum
    iconRight?: ReactNode
  } => {
    const { reasonFields } = quotationValues?.aiComplementData || {}

    if (
      reasonFields?.[key as keyof QuotationAIComplementDataReasonFieldsProps]
    ) {
      const reasonFieldsText = reasonFields?.[
        key as keyof QuotationAIComplementDataReasonFieldsProps
      ]
        ?.map((item: any) => item?.explanation) // eslint-disable-line
        .join('\n')

      return {
        iconRight: (
          <Tooltip
            text={reasonFieldsText || ''}
            widthText="15rem"
            orientation="top"
          >
            <SparksIcon className={styles['ai-icon']} />
          </Tooltip>
        ),
      }
    }

    return {}
  }

  useEffect(() => {
    if (!quotationValues?.expiresAt) {
      const dateAdded3Days = getDateWithWeekdayOffset()
      setQuotationValues((prev) => ({ ...prev, expiresAt: dateAdded3Days }))
    }
  }, [quotationValues])

  return (
    <SectionHeader
      title={`${position}. ${t('logisticDataTitle')}`}
      isComplete={isComplete}
    >
      <div className={styles.core}>
        <div className={styles['info-block']}>
          <div className={styles.title}>
            <FileIcon className={styles.icon} />
            <Text variant="title-medium">{t('logisticDataSubtitle')}</Text>
            <div className={styles['calculate-costs-button-container']}>
              <Button
                variant="ghost"
                className={styles['calculate-costs-button']}
                onClick={getCustomsCosts}
              >
                {t('logisticDataCalculateCustomsCostsButton')}
                <CalculateIcon />
              </Button>
            </div>
          </div>
          <div className={styles['info-block-content']}>
            <div className={styles['info-block-content-item']}>
              <Text variant="text-base">
                {t('logisticDataCustomsCostLabel')}{' '}
                <Tooltip
                  text={t('logisticDataCIADescription')}
                  widthText="17.25rem"
                >
                  <QuestionFillIcon />
                </Tooltip>{' '}
                <span>*</span>
              </Text>
              <InputNumber
                placeholder={t('logisticDataCustomsCostPlaceholder')}
                value={quotationValues?.customsFreightCost || 0}
                error={errors?.customsFreightCost as string}
                prefix={'$ '}
                stepSize={100}
                min={0}
                showIcons
                decimalPrecision={2}
                onChange={(newValue) =>
                  onHandleValues('customsFreightCost', newValue)
                }
                onBlur={() => {
                  getMetrics()
                }}
                {...getAIProps('customsCost')}
              />
              {validateFields.customsFreightCost(
                quotationValues?.products || [],
                t,
                styles,
              )}
            </div>

            <div className={styles['info-block-content-item']}>
              <Text variant="text-base">
                {t('logisticDataFirstMileCostLabel')}{' '}
                <Tooltip
                  text={t('logisticDataCIADescription')}
                  widthText="17.25rem"
                >
                  <QuestionFillIcon />
                </Tooltip>{' '}
                <span>*</span>
              </Text>
              <InputNumber
                placeholder={t('logisticDataFirstMileCostPlaceholder')}
                value={quotationValues?.firstMileFreightCost || 0}
                error={errors?.firstMileFreightCost as string}
                prefix={'$ '}
                stepSize={100}
                min={0}
                showIcons
                decimalPrecision={2}
                onChange={(newValue) =>
                  onHandleValues('firstMileFreightCost', newValue)
                }
                onBlur={() => {
                  getMetrics()
                }}
                {...getAIProps('firstMileCost')}
              />
            </div>

            <div className={styles['info-block-content-item']}>
              <Text variant="text-base">
                {t('logisticDataLastMileCostLabel')}{' '}
                <Tooltip
                  text={t('logisticDataCIADescription')}
                  widthText="17.25rem"
                >
                  <QuestionFillIcon />
                </Tooltip>{' '}
                <span>*</span>
              </Text>
              <InputNumber
                placeholder={t('logisticDataLastMileCostPlaceholder')}
                value={quotationValues?.lastMileFreightCost || 0}
                error={errors?.lastMileFreightCost as string}
                prefix={'$ '}
                stepSize={100}
                min={0}
                showIcons
                decimalPrecision={2}
                onChange={(newValue) => {
                  onHandleValues('lastMileFreightCost', newValue)
                }}
                onBlur={() => {
                  getMetrics()
                }}
                {...getAIProps('lastMileCost')}
              />
              {validateFields.lastMileFreightCost(
                quotationValues?.lastMileFreightCost || 0,
                t,
                styles,
              )}
            </div>
          </div>
        </div>
      </div>
    </SectionHeader>
  )
}
